import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { aFecha } from "@/components/agente/ChatWhatsApp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  BookOpen,
  CheckCircle2,
  Compass,
  FlaskConical,
  Loader2,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
  Undo2,
  X,
  XCircle,
} from "lucide-react";

// PROMPT 97 (2026-09-13): lo que el equipo le enseña al agente.
// Dos cosas distintas: "respuesta" es un dato que el cliente pregunta y hay que
// contestarle; "indicación" es una regla de comportamiento. Cada una vale siempre, o solo
// para una marca, o solo para un tour.
//
// PROMPT 129 (2026-10-15): la guía de Nueva York (PROMPT 128). El agente ya la usa sin que nadie la
// cargue; acá el equipo puede copiarla a esta lista para leerla y corregirla, apagar una respuesta
// (y volver a prenderla) y probar si el pronóstico y los eventos en vivo contestan.

const esGuia = (it) => it.categoria === "guia_ny";
const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;

// "hace 3 min" a partir de la fecha que guarda el servidor (UTC, sin la Z: aFecha la arregla).
function hace(ts) {
  const d = aFecha(ts);
  if (!d) return "";
  const min = Math.max(0, Math.round((Date.now() - d.getTime()) / 60000));
  if (min < 1) return "hace un momento";
  if (min < 60) return `hace ${min} min`;
  const h = Math.round(min / 60);
  return h < 48 ? `hace ${h} h` : `hace ${Math.round(h / 24)} días`;
}

const FUENTES = [
  ["clima", "Pronóstico oficial"],
  ["eventos", "Eventos de la Ciudad"],
];

const VACIA = {
  tipo: "respuesta",
  pregunta: "",
  respuesta: "",
  alcance: "siempre",
  marca_slug: "",
  tour_id: "",
};

export default function ConocimientoDialog({ open, onOpenChange, marcas = [], preguntaInicial = "", conversacionId = null }) {
  const [items, setItems] = useState([]);
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [busqueda, setBusqueda] = useState("");
  const [form, setForm] = useState(VACIA);
  const [editandoId, setEditandoId] = useState(null);
  const [probando, setProbando] = useState(false);
  const [prueba, setPrueba] = useState(null); // PROMPT 98: qué contestaría con esto cargado
  const [guia, setGuia] = useState(null); // PROMPT 129: el estado de la guía (GET /whatsapp/guia/estado)
  const [fuentes, setFuentes] = useState(null); // PROMPT 129: cómo salió la última prueba del clima y los eventos
  const [apagadas, setApagadas] = useState([]); // PROMPT 129: respuestas de la guía que el equipo apagó
  const [cargandoGuia, setCargandoGuia] = useState(false);
  const [probandoFuentes, setProbandoFuentes] = useState(false);
  const [vista, setVista] = useState("todas"); // PROMPT 129: todas | propias | guia

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, { data: t }, estadoGuia] = await Promise.all([
        api.get("/whatsapp/conocimiento", { params: { incluir_inactivas: true } }),
        api.get("/tours"),
        // Si el estado de la guía no se puede leer, el resto de la pantalla sigue andando.
        api.get("/whatsapp/guia/estado").then((r) => r.data).catch(() => null),
      ]);
      const todas = data?.items || [];
      setItems(todas.filter((x) => x.activo !== false));
      setApagadas(todas.filter((x) => x.activo === false && esGuia(x)));
      setGuia(estadoGuia);
      setFuentes(estadoGuia?.fuentes || null);
      setTours((Array.isArray(t) ? t : t?.items || []).filter((x) => x.activo !== false));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    cargar();
    setForm(preguntaInicial ? { ...VACIA, pregunta: preguntaInicial } : VACIA);
    setEditandoId(null);
    setVista("todas");
  }, [open, cargar, preguntaInicial]);

  const set = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const limpiar = () => {
    setForm(VACIA);
    setEditandoId(null);
    setPrueba(null);
  };

  // PROMPT 98: le hace la pregunta al agente con lo que hay cargado ahora. No toca ninguna
  // conversación ni le manda nada a nadie: es solo para ver cómo queda antes de darlo por bueno.
  const probar = async () => {
    setProbando(true);
    setPrueba(null);
    try {
      const { data } = await api.post("/whatsapp/conocimiento/probar", {
        pregunta: form.pregunta.trim(),
        marca_slug: form.alcance === "marca" ? form.marca_slug : null,
        tour_id: form.alcance === "tour" ? form.tour_id : null,
      });
      setPrueba(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProbando(false);
    }
  };

  const guardar = async () => {
    const cuerpo = {
      tipo: form.tipo,
      respuesta: form.respuesta.trim(),
      alcance: form.alcance,
      pregunta: form.tipo === "respuesta" ? form.pregunta.trim() : "",
      marca_slug: form.alcance === "marca" ? form.marca_slug : null,
      tour_id: form.alcance === "tour" ? form.tour_id : null,
    };
    if (conversacionId && !editandoId) cuerpo.conversacion_id = conversacionId;
    setGuardando(true);
    try {
      if (editandoId) await api.put(`/whatsapp/conocimiento/${editandoId}`, cuerpo);
      else await api.post("/whatsapp/conocimiento", cuerpo);
      toast.success(editandoId ? "Cambio guardado." : "Listo, el agente ya lo sabe.");
      limpiar();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const editar = (it) => {
    setForm({
      tipo: it.tipo || "respuesta",
      pregunta: it.pregunta || "",
      respuesta: it.respuesta || "",
      alcance: it.alcance || "siempre",
      marca_slug: it.marca_slug || "",
      tour_id: it.tour_id || "",
    });
    setEditandoId(it.id);
  };

  const borrar = async (it) => {
    try {
      await api.delete(`/whatsapp/conocimiento/${it.id}`);
      toast.success(
        esGuia(it)
          ? "Apagada: el agente deja de usarla. Podés volver a prenderla desde el cuadro de la guía."
          : "Quitado. El agente deja de usarlo."
      );
      if (editandoId === it.id) limpiar();
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // PROMPT 129: copia la guía de Nueva York a esta lista para leerla y corregirla. Se puede repetir:
  // crea las que faltan, pone al día las que nadie tocó y no pisa lo que el equipo editó ni prende lo que apagó.
  const cargarGuia = async () => {
    setCargandoGuia(true);
    try {
      const { data } = await api.post("/whatsapp/guia/cargar");
      const partes = [
        data.creadas ? plural(data.creadas, "respuesta cargada", "respuestas cargadas") : null,
        data.actualizadas ? plural(data.actualizadas, "puesta al día", "puestas al día") : null,
        data.editadas_sin_tocar ? plural(data.editadas_sin_tocar, "editada por el equipo (no se toca)", "editadas por el equipo (no se tocan)") : null,
        data.apagadas_sin_tocar ? plural(data.apagadas_sin_tocar, "apagada (no se toca)", "apagadas (no se tocan)") : null,
      ].filter(Boolean);
      toast.success(partes.length ? `Guía lista: ${partes.join(", ")}.` : "La guía ya estaba cargada y al día.");
      if (data.creadas) setVista("guia");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCargandoGuia(false);
    }
  };

  // PROMPT 129: una consulta chica al pronóstico oficial y al calendario de eventos para ver si contestan.
  // No le manda nada a nadie.
  const probarFuentes = async () => {
    setProbandoFuentes(true);
    try {
      const { data } = await api.post("/whatsapp/guia/fuentes");
      setFuentes(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setProbandoFuentes(false);
    }
  };

  // PROMPT 129: una respuesta de la guía que el equipo apagó con el tachito vuelve a usarse.
  const volverAPrender = async (it) => {
    try {
      await api.put(`/whatsapp/conocimiento/${it.id}`, { activo: true });
      toast.success("Listo, el agente vuelve a usarla.");
      await cargar();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const nombreTour = (id) => tours.find((t) => t.id === id)?.nombre || id;
  const nombreMarca = (slug) => marcas.find((m) => m.slug === slug)?.remitente_nombre || slug;
  const alcanceTexto = (it) =>
    it.alcance === "tour" ? nombreTour(it.tour_id) : it.alcance === "marca" ? nombreMarca(it.marca_slug) : "Siempre";

  const deLaGuia = items.filter(esGuia);
  const propias = items.filter((it) => !esGuia(it));
  const editandoGuia = Boolean(editandoId) && deLaGuia.some((it) => it.id === editandoId);

  const filtrados = items
    .filter((it) => {
      if (vista === "guia" && !esGuia(it)) return false;
      if (vista === "propias" && esGuia(it)) return false;
      if (!busqueda.trim()) return true;
      const t = `${it.pregunta || ""} ${it.respuesta || ""}`.toLowerCase();
      return t.includes(busqueda.trim().toLowerCase());
    })
    // Las de la guía van al final: son muchas y no tienen que tapar lo que el equipo enseñó.
    .sort((a, b) => Number(esGuia(a)) - Number(esGuia(b)));

  const sinNada =
    vista === "guia" && !busqueda.trim()
      ? apagadas.length > 0 && deLaGuia.length === 0
        ? "Apagaste todas las respuestas de la guía: podés volver a prenderlas desde el cuadro de arriba."
        : "La guía todavía no está cargada acá: tocá «Cargar la guía»."
      : items.length === 0 || (vista === "propias" && !busqueda.trim())
        ? "Todavía no le enseñaron nada."
        : "No hay nada con esa búsqueda.";

  const resumenGuia = guia
    ? [
        plural(guia.en_uso, "respuesta en uso", "respuestas en uso"),
        guia.cargadas ? `${guia.cargadas} de ${guia.total} cargadas acá` : "todavía sin cargar acá",
        guia.editadas?.length ? plural(guia.editadas.length, "corregida por el equipo", "corregidas por el equipo") : null,
        guia.apagadas?.length ? plural(guia.apagadas.length, "apagada", "apagadas") : null,
        guia.desactualizadas?.length
          ? plural(guia.desactualizadas.length, "con texto nuevo (se pone al día al cargar)", "con texto nuevo (se ponen al día al cargar)")
          : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "No se pudo leer el estado de la guía.";

  const puedeGuardar =
    form.respuesta.trim() &&
    (form.tipo !== "respuesta" || form.pregunta.trim()) &&
    (form.alcance !== "marca" || form.marca_slug) &&
    (form.alcance !== "tour" || form.tour_id);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="conocimiento-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" /> Qué sabe el agente
            </span>
            <Button type="button" variant="outline" size="sm" onClick={cargar} data-testid="conocimiento-refrescar" className="gap-2">
              <RefreshCw className="h-4 w-4" /> Actualizar
            </Button>
          </DialogTitle>
          <DialogDescription>
            Acá le enseñás cosas que no están en el sistema. Los horarios, los cupos y los precios ya los
            sabe: esto es para lo demás. Lo que escribas lo usa en la próxima conversación.
          </DialogDescription>
        </DialogHeader>
        <Card className="p-3 space-y-3 bg-slate-50/60" data-testid="conocimiento-form">
          {editandoGuia && (
            <p className="text-xs text-violet-700" data-testid="conocimiento-aviso-guia">
              Estás corrigiendo una respuesta de la guía de Nueva York: vale siempre, para todas las marcas y todos los tours.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Qué querés cargar</Label>
              <Select value={form.tipo} onValueChange={(v) => set("tipo", v)} disabled={editandoGuia}>
                <SelectTrigger data-testid="conocimiento-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="respuesta">Una respuesta a algo que preguntan</SelectItem>
                  <SelectItem value="instruccion">Una indicación de cómo comportarse</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Cuándo vale</Label>
              <Select value={form.alcance} onValueChange={(v) => set("alcance", v)} disabled={editandoGuia}>
                <SelectTrigger data-testid="conocimiento-alcance"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="siempre">Siempre</SelectItem>
                  <SelectItem value="marca">Solo para una marca</SelectItem>
                  <SelectItem value="tour">Solo para un tour</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          {form.alcance === "marca" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Marca</Label>
              <Select value={form.marca_slug} onValueChange={(v) => set("marca_slug", v)}>
                <SelectTrigger data-testid="conocimiento-marca"><SelectValue placeholder="Elegí la marca" /></SelectTrigger>
                <SelectContent>
                  {marcas.map((m) => (
                    <SelectItem key={m.slug} value={m.slug}>{m.remitente_nombre || m.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.alcance === "tour" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Tour</Label>
              <Select value={form.tour_id} onValueChange={(v) => set("tour_id", v)}>
                <SelectTrigger data-testid="conocimiento-tour"><SelectValue placeholder="Elegí el tour" /></SelectTrigger>
                <SelectContent>
                  {tours.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.tipo === "respuesta" && (
            <div className="space-y-1.5">
              <Label className="text-xs">Lo que pregunta el cliente</Label>
              <Input
                value={form.pregunta}
                onChange={(e) => set("pregunta", e.target.value)}
                placeholder="¿El bus tiene baño?"
                data-testid="conocimiento-pregunta"
              />
              <p className="text-[11px] text-slate-400">
                No hace falta que lo escriba igual: si pregunta parecido, el agente igual lo encuentra.
              </p>
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs">
              {form.tipo === "respuesta" ? "Qué tiene que contestar" : "Qué tiene que hacer"}
            </Label>
            <textarea
              value={form.respuesta}
              onChange={(e) => set("respuesta", e.target.value)}
              rows={3}
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900/10"
              placeholder={
                form.tipo === "respuesta"
                  ? "Los buses a Washington, Boston y Niágara tienen baño. Los tours dentro de la ciudad no, pero paramos."
                  : "Si preguntan por descuentos de grupo, decí que lo ve una persona del equipo."
              }
              data-testid="conocimiento-respuesta"
            />
          </div>
          {prueba && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2" data-testid="conocimiento-prueba">
              <p className="text-[11px] text-slate-400 mb-1">
                Con lo que hay cargado ahora, contestaría:
              </p>
              <p className="text-sm text-slate-800 whitespace-pre-wrap">{prueba.respuesta}</p>
              {(prueba.encontradas || []).length > 0 && (
                <p className="text-[11px] text-slate-400 mt-1.5">
                  Usó {prueba.encontradas.length} de las que hay cargadas.
                </p>
              )}
            </div>
          )}
          <div className="flex justify-end gap-2">
            {form.tipo === "respuesta" && form.pregunta.trim() && (
              <Button variant="outline" onClick={probar} disabled={probando} data-testid="conocimiento-probar" className="gap-1.5">
                {probando ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                Probar
              </Button>
            )}
            {editandoId && (
              <Button variant="ghost" onClick={limpiar} data-testid="conocimiento-cancelar" className="gap-1.5">
                <X className="h-4 w-4" /> Cancelar
              </Button>
            )}
            <Button onClick={guardar} disabled={guardando || !puedeGuardar} data-testid="conocimiento-guardar" className="gap-1.5">
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : editandoId ? <Save className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              {editandoId ? "Guardar el cambio" : "Enseñárselo"}
            </Button>
          </div>
        </Card>
        <Card className="p-3 space-y-2 border-violet-200 bg-violet-50/40" data-testid="guia-panel">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 flex items-center gap-1.5">
                <Compass className="h-4 w-4 text-violet-600" /> Guía de Nueva York
              </p>
              <p className="text-xs text-slate-600 mt-0.5" data-testid="guia-resumen">{resumenGuia}</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button variant="outline" size="sm" onClick={cargarGuia} disabled={cargandoGuia} data-testid="guia-cargar" className="gap-1.5">
                {cargandoGuia ? <Loader2 className="h-4 w-4 animate-spin" /> : <BookOpen className="h-4 w-4" />}
                Cargar la guía
              </Button>
              <Button variant="outline" size="sm" onClick={probarFuentes} disabled={probandoFuentes} data-testid="guia-fuentes" className="gap-1.5">
                {probandoFuentes ? <Loader2 className="h-4 w-4 animate-spin" /> : <FlaskConical className="h-4 w-4" />}
                Probar las fuentes
              </Button>
            </div>
          </div>
          <p className="text-[11px] text-slate-500">
            El agente ya usa la guía sin que la cargues: el clima y los eventos los consulta en vivo cada vez que alguien
            pregunta, y lo demás (propinas, aeropuertos, subte, ropa…) sale de estas respuestas. Cargala acá solo si querés
            leerla, corregirla o apagar alguna.
          </p>
          {fuentes ? (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 space-y-1" data-testid="guia-fuentes-resultado">
              {FUENTES.map(([clave, nombre]) => {
                const f = fuentes[clave];
                if (!f) return null;
                const segundos = typeof f.segundos === "number" ? ` (${String(f.segundos).replace(".", ",")} s)` : "";
                return (
                  <div key={clave} className="flex items-start gap-1.5 text-xs" data-testid={`guia-fuente-${clave}`}>
                    {f.ok ? (
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                    ) : (
                      <XCircle className="h-4 w-4 shrink-0 text-red-500" />
                    )}
                    <span className="min-w-0">
                      <span className="font-medium text-slate-800">{nombre}</span>
                      <span className="text-slate-600"> · {f.ok ? "contesta" : "no contestó"}: {f.detalle}{segundos}</span>
                    </span>
                  </div>
                );
              })}
              <p className="text-[11px] text-slate-400">Probado {hace(fuentes.probado_en)}.</p>
            </div>
          ) : (
            <p className="text-[11px] text-slate-400" data-testid="guia-fuentes-sin-probar">
              Todavía no se probaron el pronóstico y los eventos en vivo.
            </p>
          )}
          {apagadas.length > 0 && (
            <div className="rounded-md border border-slate-200 bg-white px-3 py-2 space-y-1" data-testid="guia-apagadas">
              <p className="text-[11px] text-slate-500">Apagadas (el agente no las usa):</p>
              {apagadas.map((it) => (
                <div key={it.id} className="flex items-center justify-between gap-2" data-testid={`guia-apagada-${it.id}`}>
                  <span className="min-w-0 truncate text-xs text-slate-700">{it.pregunta}</span>
                  <Button variant="ghost" size="sm" onClick={() => volverAPrender(it)} data-testid={`guia-prender-${it.id}`} className="shrink-0 gap-1.5">
                    <Undo2 className="h-4 w-4" /> Volver a prenderla
                  </Button>
                </div>
              ))}
            </div>
          )}
        </Card>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            placeholder="Buscar en lo que ya sabe…"
            className="pl-9"
            data-testid="conocimiento-buscar"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap" data-testid="conocimiento-vistas">
          {[
            ["todas", "Todo", items.length],
            ["propias", "Lo que le enseñamos", propias.length],
            ["guia", "Guía de Nueva York", deLaGuia.length],
          ].map(([clave, texto, n]) => (
            <button
              key={clave}
              type="button"
              onClick={() => setVista(clave)}
              data-testid={`conocimiento-vista-${clave}`}
              className={`rounded-full border px-2.5 py-1 text-xs ${
                vista === clave
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {texto} ({n})
            </button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : filtrados.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">{sinNada}</p>
        ) : (
          <div className="space-y-2">
            {filtrados.map((it) => (
              <Card key={it.id} className="p-3" data-testid={`conocimiento-${it.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-600 border-slate-200">
                        {it.tipo === "instruccion" ? "Indicación" : "Respuesta"}
                      </span>
                      {esGuia(it) ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-violet-50 text-violet-700 border-violet-200">
                          Guía de Nueva York
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-sky-50 text-sky-700 border-sky-200">
                          {alcanceTexto(it)}
                        </span>
                      )}
                      {esGuia(it) && guia?.editadas?.includes(it.clave) ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-amber-50 text-amber-700 border-amber-200">
                          Corregida por el equipo
                        </span>
                      ) : null}
                      {it.usos ? <span className="text-[10px] text-slate-400">se usó {it.usos} vez/veces</span> : null}
                    </div>
                    {it.pregunta && <p className="text-sm font-medium text-slate-900 mt-1">{it.pregunta}</p>}
                    <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap">{it.respuesta}</p>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => editar(it)} data-testid={`conocimiento-editar-${it.id}`}>
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => borrar(it)}
                      title={esGuia(it) ? "Apagar: el agente deja de usarla" : undefined}
                      data-testid={`conocimiento-borrar-${it.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-slate-400" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
